import { CreateMenuRequest } from "features/menu/createMenuSlice";
import { AlgoliaWine } from "features/winepicker/winePickerModel";
import { AppDispatch } from "lib/redux/store";
import { range } from "ramda";
import { CSSProperties } from "react";
import { toAddWineCategoryActions } from "./Editor";
import { menuEditorActions } from "./editorSlice";
import { algoliaWineToEditorWine, createEmptyDocument, createEmptyLeafletDocument, EditorItem, getAppLanguage } from "./editorUtils";
import { isPageBreakItem, MenuDocument, MenuItem } from "./MenuDocumentModel";

export interface ElementStyling {
    className: string;
    style: CSSProperties;
}

export interface EditableItemProps extends ElementStyling {
    hasControls: boolean;
}

export interface DocumentTypeTranslations {
    successEmailFirstLine: string;
    createMenuTitle: string;
    createMenuName: string;
}

export abstract class DocumentHandler {
    constructor(protected document: MenuDocument) {}

    abstract handleAddWines(algoliaWines: AlgoliaWine[], dispatch: AppDispatch): void;
    shouldBreakPageAfterItem(item: MenuItem): boolean {
        return isPageBreakItem(item);
    }
    getEditablePageWrapperStyling(): ElementStyling {
        return {
            className: this.document.pages.length === 0 ? 'h-full' : '',
            style: {}
        }
    }
    abstract getPageStyling(mode: 'preview'|'pdf'): ElementStyling
    getEditableItemPropsAndStyling(item: EditorItem<MenuItem>, isFocused: boolean): EditableItemProps {
        return {
            className: isFocused?"outline outline-primary":"",
            style: {},
            hasControls: false
        }
    }
    isItemSortable(item: MenuItem): boolean {
        return true
    }
    getNumberOfPrints(): number[] {
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 25, 30, 35, 40, 45, 50]
    }
    unfocusActiveItem(dispatch: AppDispatch) {
        dispatch(menuEditorActions.setFocusedItem(null));
    }
}

export abstract class DocumentTypeHandler {
    constructor(protected type: MenuDocument['type']) {}
    abstract createEmptyDocument(createMenuRequest: CreateMenuRequest): MenuDocument;
    abstract getTranslations(): DocumentTypeTranslations;
}

export class WineMenuDocumentTypeHandler extends DocumentTypeHandler {
    createEmptyDocument(createMenuRequest: CreateMenuRequest): MenuDocument {
        return createEmptyDocument(createMenuRequest.language);
    }
    getTranslations(): DocumentTypeTranslations {
        return {
            successEmailFirstLine: "editor.print.successEmail.firstLine",
            createMenuTitle: "create.title",
            createMenuName: "create.name"
        }
    }
}

export class LeafletDocumentTypeHandler extends DocumentTypeHandler {
    createEmptyDocument(createMenuRequest: CreateMenuRequest): MenuDocument {
        return createEmptyLeafletDocument(createMenuRequest.language);
    }
    getTranslations(): DocumentTypeTranslations {
        return {
            successEmailFirstLine: "editor.print.successEmail.firstLine.leaflet",
            createMenuTitle: "create.title.leaflet",
            createMenuName: "create.name.leaflet"
        }
    }
}

export class WineMenuDocumentHandler extends DocumentHandler {
    handleAddWines(algoliaWines: AlgoliaWine[], dispatch: AppDispatch) {
        const language = this.document.language ?? getAppLanguage() ?? 'de';
        
        const addActions = toAddWineCategoryActions(language, algoliaWines);
        addActions.forEach(a => {
            dispatch(menuEditorActions.addWineCategory(a));
        });
    }
    getEditablePageWrapperStyling(): ElementStyling {
        const styling = super.getEditablePageWrapperStyling();
        styling.className += ` py-2 gap-2
            ${this.document.format === 'a4' && 'scale-75 origin-top'}
        `;
        return styling;
    }
    getEditableItemPropsAndStyling(item: EditorItem<MenuItem>, isFocused: boolean): EditableItemProps {
        const styles = super.getEditableItemPropsAndStyling(item, isFocused);
        styles.className += " rounded";
        if (isFocused) {
            styles.style = {
                ...styles.style,
                marginRight: '-3rem',
                paddingRight: '3rem'
            }
            styles.className += " outline-2 outline-offset-8"
        }
        styles.hasControls = true;
        return styles;
    }
    getPageStyling(mode: "preview" | "pdf"): ElementStyling {
        const formatClassA4 =     'w-a4-width      h-a4-height      py-a4-page-margin-y      px-a4-page-margin-x     ';
        const formatClassA4half = 'w-a4-half-width h-a4-half-height py-a4-half-page-margin-y px-a4-half-page-margin-x';
        const formatClassA5 =     'w-a5-width      h-a5-height      py-a5-page-margin-y      px-a5-page-margin-x     ';
        const format = this.document.format;
        let className = (
            format === 'a5'      ? formatClassA5 :
            format === 'a4-half' ? formatClassA4half :
            format === 'a4'      ? formatClassA4 :
            ''
        )
        if (mode === 'pdf') className += ' m-[2mm]';
        return {className, style:{}};
    }
}


export class LeafletDocumentHandler extends DocumentHandler {
    handleAddWines(algoliaWines: AlgoliaWine[], dispatch: AppDispatch) {
        const language = this.document.language ?? getAppLanguage() ?? 'de';
        const items = algoliaWines.map(algoliaWine => {
            const wine = algoliaWineToEditorWine(language, algoliaWine);
            wine.prices = this.document.sizes.map(()=>0);
            wine.showSuitableFood = false;
            return wine;
        });
        dispatch(menuEditorActions.addItems({items}));
    }
    shouldBreakPageAfterItem(item: MenuItem): boolean {return true}
    getEditablePageWrapperStyling(): ElementStyling {
        const styling = super.getEditablePageWrapperStyling();
        styling.className += " py-4 gap-4";
        return styling;
    }
    getPageStyling(mode: "preview" | "pdf"): ElementStyling {
        const formatClassA6 =     'w-a6-width      h-a6-height      py-a6-page-margin-y      px-a6-page-margin-x      ';
        let className = formatClassA6;
        if (mode === 'pdf') className += 'm-[2mm]';
        return {className,style: {}}
    }
    getEditableItemPropsAndStyling(item: EditorItem<MenuItem>, isFocused: boolean): EditableItemProps {
        const styles = super.getEditableItemPropsAndStyling(item, isFocused);
        styles.className += " outline-offset-8"
        if (isFocused) {
            styles.style = {
                ...styles.style,
                outlineOffset: '8mm'
            }
        }
        return styles;
    }
    getNumberOfPrints(): number[] {
        return range(1, 20).map(i=>i*4);
    }
    isItemSortable(item: MenuItem): boolean {
        return false;
    }
}
